import { useDebounceFn } from '@vueuse/core';
import { toRaw, watch } from 'vue';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { useMapPersistenceStore } from '#nora-ui/map/stores/map-persistence-store';
import type { OfflineMapState } from '#nora-ui/map/types/persistence.types';
import { OFFLINE_STATE_VERSION } from '#nora-ui/map/types/persistence.types';

/**
 * Debounce delay in milliseconds for persistence operations.
 */
const PERSISTENCE_DEBOUNCE_MS = 1000;

/**
 * MapPersistenceManager orchestrates offline state persistence for a NoraMap instance.
 *
 * It collects state from all Persistable managers, serializes it to a single
 * OfflineMapState object, and persists it via the mapPersistenceStore.
 *
 * On hydration, it restores state from the persisted snapshot back to the managers.
 */
export class MapPersistenceManager {
  private persistenceStore = useMapPersistenceStore();
  private stopWatchers: (() => void)[] = [];

  constructor(private readonly map: NoraMap) {}

  /**
   * Serialize the current state of all Persistable managers into an OfflineMapState.
   */
  serialize(): OfflineMapState {
    return {
      version: OFFLINE_STATE_VERSION,
      mapConfig: toRaw(this.map.ref.getMapConfig()),
      drawState: this.map.state.draw.serialize(),
      trackState: this.map.state.track.serialize(),
      markerState: this.map.state.marker.serialize(),
      fileState: this.map.state.file.serialize(),
      notificationState: this.map.state.notification.serialize(),
      drawToolUI: this.map.drawToolUI.serialize(),
    };
  }

  /**
   * Restore state from a persisted OfflineMapState snapshot.
   * Called during map initialization if persisted state exists.
   *
   * @returns true if hydration was successful, false otherwise (e.g. version mismatch).
   */
  deserialize(state: OfflineMapState): boolean {
    // Check version compatibility
    if (state.version !== OFFLINE_STATE_VERSION) {
      console.warn(
        `[MapPersistenceManager] State version mismatch: expected ${OFFLINE_STATE_VERSION}, got ${state.version}. Skipping hydration.`,
      );

      return false;
    }

    // Restore state to each manager if the snapshot contains data for it
    if (state.drawState) {
      this.map.state.draw.deserialize(state.drawState);
    }

    if (state.trackState) {
      this.map.state.track.deserialize(state.trackState);
    }

    if (state.markerState) {
      this.map.state.marker.deserialize(state.markerState);
    }

    if (state.fileState) {
      this.map.state.file.deserialize(state.fileState);
    }

    if (state.notificationState) {
      this.map.state.notification.deserialize(state.notificationState);
    }

    if (state.drawToolUI) {
      this.map.drawToolUI.deserialize(state.drawToolUI);
    }

    return true;
  }

  /**
   * Hydrate state managers from persisted state if available.
   * Should be called during map setup, before watchers are set up.
   */
  hydrate(): boolean {
    const mapId = this.map.getId();
    const persistedState = this.persistenceStore.getMapState(mapId);

    if (persistedState) {
      // eslint-disable-next-line no-console
      console.debug(`[MapPersistenceManager] Hydrating map ${mapId} from persisted state`);

      if (this.deserialize(persistedState)) {
        return true;
      }

      // If hydration failed (e.g. version mismatch), remove the invalid state
      // and return false so map initialization continues as if no state existed.
      this.persistenceStore.removeMapState(mapId);
    }

    // eslint-disable-next-line no-console
    console.debug(`[MapPersistenceManager] No persisted state found for map ${mapId}`);
    return false;
  }

  /**
   * Persist the current state to the persistence store.
   */
  persist(): void {
    try {
      const mapId = this.map.getId();
      const state = this.serialize();

      this.persistenceStore.setMapState(mapId, state);

      // eslint-disable-next-line no-console
      console.debug(`[MapPersistenceManager] Persisted state for map ${mapId}`);
    }
    catch (error) {
      console.error('[MapPersistenceManager] Error persisting map state:', error);
    }
  }

  /**
   * Set up watchers on all Persistable managers to trigger persistence on state changes.
   * Uses debouncing to prevent excessive writes.
   */
  setupWatchers(): void {
    const debouncedPersist = useDebounceFn(() => this.persist(), PERSISTENCE_DEBOUNCE_MS, {
      maxWait: PERSISTENCE_DEBOUNCE_MS * 2,
    });

    // Collect watch sources from all Persistable managers
    const watchSources = [
      this.map.state.draw.getWatchSource(),
      this.map.state.track.getWatchSource(),
      this.map.state.marker.getWatchSource(),
      this.map.state.file.getWatchSource(),
      this.map.state.notification.getWatchSource(),
      this.map.drawToolUI.getWatchSource(),
      // Also watch mapConfig for viewport changes
      () => this.map.ref.getMapConfig(),
    ];

    // Set up a single watch that monitors all sources
    const stopWatch = watch(
      watchSources,
      async () => {
        await debouncedPersist();
      },
      { deep: true },
    );

    this.stopWatchers.push(stopWatch);
  }

  /**
   * Clean up watchers when the map is destroyed.
   */
  cleanup(): void {
    for (const stopWatcher of this.stopWatchers) {
      stopWatcher();
    }

    this.stopWatchers = [];
  }

  /**
   * Remove persisted state for this map.
   * Called when the map is permanently removed.
   */
  removePersistedState(): void {
    const mapId = this.map.getId();
    this.persistenceStore.removeMapState(mapId);

    // eslint-disable-next-line no-console
    console.debug(`[MapPersistenceManager] Removed persisted state for map ${mapId}`);
  }
}
